import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import XIcon from "@redotech/redo-web/arbiter-icon/x-close.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { Modal } from "../../modal";
import { ZipCodeContext } from "./zip-code-context";
import * as css from "./zip-code-modal.module.css";

export const ZipCodeModal = memo(function ZipCodeModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const zipContext = useContext(ZipCodeContext);

  const [localZipCode, setLocalZipCode] = useState<string | undefined>(
    undefined,
  );
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSaveZip = useHandler((zipCodeToSave: string) => {
    if (!zipContext) {
      return;
    }

    const { saveZipCode } = zipContext;
    try {
      saveZipCode(zipCodeToSave);
      onClose();
    } catch (error) {
      // Handle invalid zip code error
      console.error("Invalid zip code:", error);
      setErrorText("Invalid zip code");
    }
  });

  useEffect(() => {
    if (zipContext) {
      setLocalZipCode(zipContext.savedZipCode);
    }
  }, [zipContext]);

  // Focus input when modal opens using requestAnimationFrame
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }
  }, [open]);

  if (!zipContext) {
    return null;
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Flex dir="column" gap="md" p="xl" position="relative">
        <RedoButton
          className={css.closeButton}
          IconLeading={XIcon}
          onClick={onClose}
          size="xs"
        />
        <Flex
          align="flex-end"
          as="form"
          dir="row"
          onSubmit={(event) =>
            // Prevent page reload
            event.preventDefault()
          }
        >
          <RedoTextInput
            label="Zip/postal code"
            placeholder="Enter your zip code"
            ref={inputRef}
            setValue={(value) => {
              setLocalZipCode(value);
              setErrorText(undefined);
            }}
            value={localZipCode ?? ""}
          />
          <RedoButton
            disabled={!localZipCode}
            onClick={() => {
              if (localZipCode) {
                handleSaveZip(localZipCode);
              }
            }}
            text="Save"
            type="submit"
          />
        </Flex>
        {errorText && (
          <Text fontSize="xs" textColor="error">
            {errorText}
          </Text>
        )}
      </Flex>
    </Modal>
  );
});
